import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import CtaBand from "../components/CtaBand";
import { getArticle, ARTICLES } from "../data/articles";

const ArticlePage = () => {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <Navigate to="/approfondimenti" replace />;

  const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <>
      <Seo
        title={`${article.title} | Avv. Manfredi Bruno`}
        description={article.metaDescription}
        path={`/approfondimenti/${article.slug}`}
        type="article"
      />

      <article className="bg-white" data-testid="article-page">
        <header className="border-b border-slate-200 bg-gold-soft/60 py-12 sm:py-16">
          <div className="container-site max-w-3xl">
            <Link
              to="/approfondimenti"
              data-testid="article-back-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark transition-transform duration-300 hover:-translate-x-1"
            >
              <ArrowLeft size={15} aria-hidden="true" /> Tutti gli approfondimenti
            </Link>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
              <span className="rounded-full bg-white px-3 py-1 font-semibold uppercase tracking-wide text-gold-dark">
                {article.category}
              </span>
              <time dateTime={article.date} className="text-slate-500">{article.dateLabel}</time>
              <span className="text-slate-400">· {article.readingTime} di lettura</span>
            </div>
            <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-navy">
              {article.title}
            </h1>
            <p className="mt-3 text-sm text-slate-500">
              A cura dell'Avv. Manfredi Bruno — Studio Legale, Palermo
            </p>
          </div>
        </header>

        <div className="container-site max-w-3xl py-12 sm:py-16">
          <div className="legal-prose">
            {article.blocks.map((b, i) => {
              if (b.type === "h2") return <h2 key={i}>{b.text}</h2>;
              if (b.type === "link")
                return (
                  <Link
                    key={i}
                    to={b.href}
                    data-testid={`article-inline-link-${i}`}
                    className="group my-8 flex items-center justify-between gap-4 rounded-2xl border border-gold/30 bg-gold-soft p-5 transition-all duration-300 hover:border-gold hover:shadow-md"
                  >
                    <span>
                      <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-gold-dark">
                        {b.label}
                      </span>
                      <span className="mt-1 block font-serif text-lg font-semibold text-navy">
                        {b.text}
                      </span>
                    </span>
                    <ArrowRight size={18} className="shrink-0 text-gold-dark transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                );
              if (b.type === "list")
                return (
                  <ul key={i}>
                    {b.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                );
              return <p key={i}>{b.text}</p>;
            })}
          </div>
          <p className="mt-10 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs leading-relaxed text-slate-500">
            Questo articolo ha finalità puramente informative e non costituisce
            consulenza legale. Ogni situazione richiede una valutazione specifica:
            per il tuo caso concreto puoi richiedere un appuntamento presso lo studio.
          </p>
        </div>
      </article>

      <section className="bg-gold-soft py-14">
        <div className="container-site">
          <h2 className="font-serif text-2xl font-semibold text-navy">Continua a leggere</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {others.map((a) => (
              <Link
                key={a.slug}
                to={`/approfondimenti/${a.slug}`}
                data-testid={`related-article-${a.slug}`}
                className="card-elegant group flex items-center justify-between gap-4"
              >
                <span className="font-serif text-lg font-semibold leading-snug text-navy">{a.title}</span>
                <ArrowRight size={18} className="shrink-0 text-gold-dark transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
};

export default ArticlePage;
